/**
 * Careers page content — 100% fidelity to content/company/careers.md
 * Cross-reference: content/company/careers.md
 * 18 sections, all handcrafted from source.
 */

export const careersMeta = {
  title: "Careers | Perception X2 Join Our Team",
  description:
    "Join Perception X2 and shape the future of autonomous perception amplification. Explore open roles in AI engineering, strategy, operations, and creative intelligence.",
  slug: "careers",
  canonical: "https://perception.ac/careers",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  trustIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Careers at Perception X2",
  title: "Build What Others Cannot.",
  titleAccent: "Shape What Others Cannot See.",
  lede: "Perception X2 is hiring exceptional minds who want to work on the most advanced autonomous perception amplification platform in existence. We do not build ordinary software. We engineer systems that shape how the world perceives reality — across 50+ platforms, in real time, at scale.",
  body: "This is not a conventional tech company. We operate at the intersection of artificial intelligence, cognitive science, and strategic communications. Our nine proprietary AI engines process millions of data points daily, generating 300-500% improvements in narrative penetration while maintaining 95-98% organic authenticity. The work is demanding. The impact is measurable. The team is elite.",
  cta: [
    { label: "View Open Positions", href: "#open-roles" },
    { label: "Learn About Our Culture", href: "#our-culture" },
  ],
  trustIndicators: [
    "15+ Years of Operational Excellence",
    "300+ Elite Global Clients",
    "Nine Proprietary AI Engines",
    "Zero Security Incidents · 99.9999% Uptime",
  ],
};

export interface WhyJoinData {
  eyebrow: string;
  title: string;
  paragraphs: string[];
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  intro?: string;
}

export const whyJoin: WhyJoinData = {
  eyebrow: "Why Join Perception X2",
  title: "Work That Matters. Outcomes That Compound.",
  paragraphs: [
    "At Perception X2, every engineer, strategist, and operator contributes to systems that deliver measurable, real-world impact. We do not build features that gather dust. We build capabilities that shape narratives, protect reputations, and influence outcomes at global scale.",
    "Our team members are not here for free snacks and ping-pong tables. They are here because the problems are genuinely hard, the technology is genuinely advanced, and the results are genuinely significant. When you work on a platform that processes 144-dimensional emotional resonance data and deploys autonomous content intelligence across 50+ platforms, you solve problems that do not exist at other companies.",
    "We serve organizations where perception determines outcomes — governments, Fortune 500 enterprises, global institutions. The stakes are high. The bar is higher. Every line of code, every strategy, every deployment must meet a standard that conventional organizations never encounter. This is not a comfortable place to coast. It is an exceptional place to grow.",
    "Our nine AI engines — Organic Simulation, Narrative Generation, Cognitive Resonance, Echo Chamber Architecture, and five others — are not open-source libraries. They are 15+ years of proprietary research, engineering, and refinement. Working here means working on technology that cannot be found anywhere else.",
  ],
  pillars: [
    { title: "Why Top Talent Chooses Us", body: "We hire for capability and character. The best ideas win regardless of source." },
    { title: "The Work Is Real", body: "Real-world impact serving governments, Fortune 500 enterprises, and global institutions." },
    { title: "The Technology Is Proprietary", body: "Nine AI engines refined over 15+ years. Not licensed. Not open-source. Built." },
  ],
  cta: [
    { label: "Explore Open Positions", href: "#open-roles" },
    { label: "See Our Values", href: "#our-values" },
  ],
};

export interface CultureData {
  eyebrow: string;
  title: string;
  intro: string;
  dimensions: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const culture: CultureData = {
  eyebrow: "Our Culture",
  title: "Precision. Discretion. Impact.",
  intro: "Perception X2 operates with the precision of a defense contractor and the ambition of a technology pioneer. Our culture is built on three non-negotiable principles: excellence in execution, absolute discretion, and measurable impact.",
  dimensions: [
    {
      title: "How We Work",
      body: "We are a results-first organization. We measure outcomes, not hours. We measure impact, not activity. Every team member understands how their work connects to client outcomes — because transparency about mission is fundamental to executing that mission.",
    },
    {
      title: "How We Communicate",
      body: "Direct. Honest. Efficient. We do not dress up mediocrity in corporate language. We do not hide behind ambiguity. When something works, we say it works. When something needs improvement, we say that too. Every communication — internal or external — meets the same standard of clarity and precision.",
    },
    {
      title: "How We Collaborate",
      body: "Cross-functional teams operate with clear ownership and shared accountability. Engineers work directly with strategists. Data scientists work directly with creative teams. Silos do not survive here because they produce suboptimal outcomes — and suboptimal outcomes are not acceptable.",
    },
    {
      title: "How We Protect",
      body: "Security is not a department. It is a culture. Every team member understands their responsibility in maintaining zero security incidents. End-to-end encryption, compartmentalized operations, and strict access controls are not policies we follow — they are habits we live.",
    },
  ],
  cta: [
    { label: "Learn About Our Values", href: "#our-values" },
    { label: "See Open Roles", href: "#open-roles" },
  ],
};

export interface ValuesData {
  eyebrow: string;
  title: string;
  intro: string;
  rows: { value: string; meaning: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const values: ValuesData = {
  eyebrow: "Our Values",
  title: "Five Principles. Zero Compromise.",
  intro: "Every decision at Perception X2 is filtered through five core values. These are not aspirational statements on a wall. They are operational standards that govern hiring, promotion, project prioritization, and daily decision-making.",
  rows: [
    { value: "Excellence", meaning: "Every output meets the highest possible standard. Good enough is not good enough.", body: "We serve organizations where failure is not an option. This demands a team that holds itself to the same standard. We hire for excellence. We promote for excellence. We remove anything less." },
    { value: "Discretion", meaning: "Absolute confidentiality in every operation. Zero tolerance for leaks, breaches, or careless handling.", body: "Our clients trust us with their most sensitive operations. That trust is earned through 15+ years of zero security incidents. Every team member — from engineers to executives — operates under strict confidentiality protocols. No exceptions." },
    { value: "Impact", meaning: "Every action ties to measurable outcomes. Activity without impact is waste.", body: "We do not measure success by lines of code or hours billed. We measure success by narrative penetration, organic authenticity ratings, and client outcomes. Every team member should be able to articulate how their work creates impact." },
    { value: "Innovation", meaning: "Continuous advancement of technology and methodology. Standing still is falling behind.", body: "Nine proprietary AI engines exist because we refused to rely on third-party solutions. We build what we need, test it rigorously, and deploy it with confidence. Complacency is the enemy of innovation." },
    { value: "Integrity", meaning: "Ethical practice in every engagement. We decline work that conflicts with our principles.", body: "We work exclusively with legitimate organizations pursuing lawful objectives. We decline projects with questionable motives. This boundary is non-negotiable and applies equally to every engagement." },
  ],
  cta: [
    { label: "Join Our Team", href: "#open-roles" },
    { label: "Learn About Benefits", href: "#benefits--compensation" },
  ],
};

export interface WhatWeDoData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const whatWeDo: WhatWeDoData = {
  eyebrow: "What We Do",
  title: "Autonomous Perception Amplification at Global Scale",
  intro: "Perception X2 is the world's most advanced autonomous perception amplification platform. We operate across 50+ digital platforms, deploying nine proprietary AI engines to shape, protect, and amplify digital presence for organizations where perception determines outcomes.",
  pillars: [
    {
      title: "The Platform",
      body: "Our autonomous AI platform analyzes digital landscapes, develops narrative strategies, creates authentic content, and coordinates deployment across 50+ platforms — continuously optimizing for maximum impact. The platform processes millions of data points daily, achieving 300-500% improvement in narrative penetration while maintaining 95-98% organic authenticity.",
    },
    {
      title: "The Technology",
      body: "Nine proprietary AI engines power the platform: Organic Simulation Engine, Narrative Generation Engine, Cognitive Resonance Engine, Echo Chamber Architecture, and five additional engines purpose-built for specific operational functions. Together, they deliver capabilities that cannot be replicated by any other organization.",
    },
    {
      title: "The Impact",
      body: "We serve governments, Fortune 500 enterprises, political organizations, public figures, defense agencies, and financial institutions. Every engagement delivers measurable outcomes — 300-500% narrative penetration improvement, real-time crisis response, and competitive advantage that compounds over time.",
    },
  ],
  cta: [
    { label: "Explore Our Platform", href: "#what-we-do" },
    { label: "See Open Roles", href: "#open-roles" },
  ],
};

export interface DepartmentsData {
  eyebrow: string;
  title: string;
  intro: string;
  departments: { title: string; body: string; icon: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const departments: DepartmentsData = {
  eyebrow: "Departments",
  title: "Where Your Talent Fits",
  intro: "Perception X2 operates through specialized departments, each responsible for a critical component of the platform and its operations. Understanding our structure helps identify where your skills create the most impact.",
  departments: [
    { title: "Engineering", body: "The backbone of Perception X2. Our engineering teams build and maintain the infrastructure that powers nine AI engines across 50+ platforms. Responsibilities include distributed systems, API development, database architecture, security engineering, and platform reliability.", icon: "Cpu" },
    { title: "Artificial Intelligence & Machine Learning", body: "The intelligence layer. Our AI/ML teams develop and refine the algorithms that drive narrative generation, cognitive resonance mapping, organic simulation, and predictive analytics. This team works on problems that do not have Stack Overflow answers.", icon: "Brain" },
    { title: "Strategy & Operations", body: "The strategic layer. Our strategists translate platform capabilities into client outcomes. Responsibilities include campaign strategy, narrative architecture, competitive analysis, performance optimization, and client success management.", icon: "Telescope" },
    { title: "Creative & Content Intelligence", body: "The creative layer. Our creative teams develop content frameworks, narrative templates, and content intelligence systems that maintain 95-98% organic authenticity across all platforms. This team understands what resonates — and why.", icon: "Sparkles" },
    { title: "Data Science & Analytics", body: "The analytical layer. Our data scientists build the models that power 144-dimensional emotional resonance mapping, sentiment analysis, trend prediction, and performance analytics. Data drives every decision.", icon: "BarChart3" },
    { title: "Security & Compliance", body: "The protective layer. Our security team maintains the infrastructure, protocols, and culture that have produced zero security incidents across 15+ years of operations. This team is relentless.", icon: "Shield" },
    { title: "Client Success", body: "The relationship layer. Our client success teams ensure every engagement delivers measurable outcomes. Responsibilities include onboarding, performance monitoring, strategic consulting, and long-term relationship management.", icon: "Users" },
  ],
  cta: [
    { label: "Find Your Department", href: "#departments" },
    { label: "Explore Open Roles", href: "#open-roles" },
  ],
};

export interface RolesData {
  eyebrow: string;
  title: string;
  intro: string;
  bullets: string[];
  requirements: string[];
  cta: { label: string; href: string }[];
  rows: { role: string; focus: string }[];
  pillars?: { title: string; body: string }[];
}

export const engineeringRoles: RolesData = {
  eyebrow: "Engineering Roles",
  title: "Build the Infrastructure That Shapes Perception",
  intro: "Our engineering teams build and maintain the distributed systems, APIs, and platform infrastructure that power the world's most advanced perception amplification engine. If you thrive on solving hard problems at scale, this is where you belong.",
  rows: [
    { role: "Senior Distributed Systems Engineer", focus: "Multi-region platform architecture, failover systems, 99.9999% uptime infrastructure" },
    { role: "Platform Reliability Engineer", focus: "Monitoring, alerting, incident response, 2,000+ endpoint health checks" },
    { role: "Senior API Engineer", focus: "RESTful API design, 45,000+ requests/second, real-time data processing" },
    { role: "Security Engineer", focus: "Zero-trust architecture, AES-256 encryption, penetration testing" },
    { role: "Database Architect", focus: "High-performance data storage, query optimization, multi-region replication" },
    { role: "Frontend Engineer", focus: "Dashboard development, real-time analytics visualization, responsive interfaces" },
  ],

  bullets: [
    "Distributed systems processing millions of data points daily",
    "APIs handling 45,000+ requests per second with sub-50ms latency",
    "Security infrastructure maintaining zero incidents across 15+ years",
    "Multi-region deployment with automatic failover within 500ms",
    "Real-time monitoring across 2,000+ health endpoints",
  ],
  requirements: [
    "5+ years in distributed systems, infrastructure, or platform engineering",
    "Experience with high-availability systems (99.99%+ uptime)",
    "Strong understanding of security principles and encryption standards",
    "Ability to work in a high-stakes, precision-oriented environment",
    "Excellent written and verbal communication",
  ],
  cta: [
    { label: "Apply Now", href: "#how-to-apply" },
    { label: "See All Roles", href: "#open-roles" },
  ],
};

export const aiRoles: RolesData = {
  eyebrow: "AI & Machine Learning Roles",
  title: "Engineer Intelligence That Shapes Reality",
  intro: "Our AI and machine learning teams build the algorithms that power nine proprietary engines — from narrative generation to cognitive resonance mapping to organic simulation. This is not applied ML using off-the-shelf models. This is fundamental research and engineering that produces capabilities no other organization possesses.",
  rows: [
    { role: "Senior NLP Engineer", focus: "Natural Language Processing across 100+ languages, content authenticity engineering" },
    { role: "Machine Learning Engineer — Predictive Analytics", focus: "Trend prediction, narrative trajectory forecasting, competitive intelligence" },
    { role: "Cognitive Resonance Scientist", focus: "144-dimensional emotional resonance mapping, audience psychology modeling" },
    { role: "Organic Simulation Engineer", focus: "Persona modeling, narrative propagation simulation, deployment optimization" },
    { role: "Senior Data Scientist", focus: "Sentiment analysis, performance analytics, 144-dimensional mapping models" },
    { role: "AI Ethics & Safety Engineer", focus: "Responsible AI development, bias detection, authenticity verification" },
  ],

  bullets: [
    "Natural Language Processing engines across 100+ languages",
    "144-dimensional emotional resonance mapping models",
    "Predictive analytics with industry-leading accuracy",
    "Persona simulation systems modeling 10,000-1,000,000+ entities",
    "Content authenticity verification achieving 95-98% organic ratings",
  ],
  requirements: [
    "5+ years in machine learning, NLP, or data science",
    "Published research or demonstrable expertise in relevant domains",
    "Experience building production ML systems at scale",
    "Strong mathematical foundations in statistics and probability",
    "Comfort working with ambiguous, novel problems",
  ],
  cta: [
    { label: "Apply Now", href: "#how-to-apply" },
    { label: "See All Roles", href: "#open-roles" },
  ],
};

export const strategyRoles: RolesData = {
  eyebrow: "Strategy & Operations Roles",
  title: "Translate Intelligence Into Outcomes",
  intro: "Our strategy and operations teams bridge the gap between platform capabilities and client outcomes. These roles require a rare combination of analytical rigor, strategic thinking, and operational discipline. If you can see patterns in complexity and execute with precision, this is your arena.",
  rows: [
    { role: "Senior Perception Strategist", focus: "Campaign strategy, narrative architecture, competitive positioning" },
    { role: "Operations Manager — Global Campaigns", focus: "Multi-platform deployment coordination, campaign optimization" },
    { role: "Intelligence Analyst", focus: "Threat assessment, trend analysis, competitive intelligence gathering" },
    { role: "Client Success Director", focus: "Relationship management, performance optimization, strategic consulting" },
    { role: "Campaign Performance Analyst", focus: "Real-time analytics, 144-dimensional resonance tracking, ROI measurement" },
    { role: "Crisis Response Coordinator", focus: "Rapid-response deployment, counter-narrative coordination, incident management" },
  ],

  bullets: [
    "Developing narrative strategies for Fortune 500 enterprises and sovereign entities",
    "Coordinating multi-platform campaigns across 50+ digital surfaces",
    "Analyzing 144-dimensional emotional resonance data to optimize targeting",
    "Managing real-time crisis response with hours, not days, response times",
    "Delivering measurable outcomes: 300-500% narrative penetration improvement",
  ],
  requirements: [
    "5+ years in strategic communications, campaign management, or intelligence analysis",
    "Experience working with high-stakes, confidential operations",
    "Strong analytical skills with comfort in data-driven decision-making",
    "Excellent written communication — precision and clarity are non-negotiable",
    "Ability to operate under pressure with absolute discretion",
  ],
  cta: [
    { label: "Apply Now", href: "#how-to-apply" },
    { label: "See All Roles", href: "#open-roles" },
  ],
};

export const creativeRoles: RolesData = {
  eyebrow: "Creative & Content Roles",
  title: "Engineer What Resonates",
  intro: "Our creative and content intelligence teams develop the frameworks, templates, and systems that produce content with 95-98% organic authenticity across 50+ platforms. This is not conventional content marketing. This is content engineering — understanding what resonates at a cognitive level and building systems that replicate it at scale.",
  rows: [
    { role: "Senior Content Strategist", focus: "Narrative frameworks, content architecture, audience targeting" },
    { role: "Content Intelligence Engineer", focus: "AI-assisted content optimization, authenticity verification, platform adaptation" },
    { role: "Visual Narrative Designer", focus: "Visual content frameworks, infographic design, brand consistency" },
    { role: "Copywriting Director — Narrative Engineering", focus: "High-impact messaging, multi-platform content adaptation, tone calibration" },
    { role: "Content Performance Analyst", focus: "Content analytics, resonance tracking, engagement optimization" },
  ],

  bullets: [
    "Developing content frameworks that maintain 95-98% organic authenticity",
    "Engineering narratives for deployment across 50+ platforms simultaneously",
    "Analyzing 144-dimensional emotional resonance data to optimize content",
    "Creating content libraries spanning text, image, and video formats",
    "Balancing authenticity with strategic messaging at scale",
  ],
  requirements: [
    "5+ years in content strategy, narrative engineering, or creative direction",
    "Portfolio demonstrating high-impact, data-driven content work",
    "Understanding of audience psychology and emotional resonance",
    "Experience with multi-platform content deployment",
    "Writing that is precise, compelling, and free of unnecessary embellishment",
  ],
  cta: [
    { label: "Apply Now", href: "#how-to-apply" },
    { label: "See All Roles", href: "#open-roles" },
  ],
};

export interface BenefitsData {
  eyebrow: string;
  title: string;
  intro: string;
  compensation: { component: string; details: string }[];
  health: { benefit: string; coverage: string }[];
  professional: { benefit: string; details: string }[];
  flexibility: { benefit: string; details: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const benefits: BenefitsData = {
  eyebrow: "Benefits & Compensation",
  title: "Exceptional People Deserve Exceptional Terms",
  intro: "Perception X2 offers competitive compensation, comprehensive benefits, and an environment designed for sustained high performance. We invest in our team because our team is our competitive advantage.",
  compensation: [
    { component: "Base Salary", details: "Market-leading, reviewed annually" },
    { component: "Performance Bonus", details: "Tied to individual and company outcomes" },
    { component: "Equity", details: "Available for senior roles and key contributors" },
    { component: "Sign-On Bonus", details: "Competitive packages for exceptional candidates" },
  ],
  health: [
    { benefit: "Medical Insurance", coverage: "Comprehensive global coverage" },
    { benefit: "Dental & Vision", coverage: "Full coverage for employees and dependents" },
    { benefit: "Mental Health", coverage: "Confidential counseling and support services" },
    { benefit: "Wellness Stipend", coverage: "Annual allowance for fitness, nutrition, or wellness" },
    { benefit: "Paid Leave", coverage: "Generous vacation, sick leave, and personal days" },
  ],
  professional: [
    { benefit: "Learning Budget", details: "Annual allowance for courses, conferences, and certifications" },
    { benefit: "Conference Attendance", details: "Industry events and speaking opportunities" },
    { benefit: "Internal Training", details: "Access to proprietary training and knowledge base" },
    { benefit: "Mentorship", details: "Pairing with senior leaders across departments" },
    { benefit: "Career Pathing", details: "Clear progression frameworks for every role" },
  ],
  flexibility: [
    { benefit: "Remote Work", details: "Flexible remote-first options" },
    { benefit: "Global Operations", details: "Work from multiple operational regions" },
    { benefit: "Equipment", details: "Premium hardware and software provided" },
    { benefit: "Home Office Stipend", details: "Setup allowance for remote work" },
  ],
  cta: [
    { label: "Join Our Team", href: "#open-roles" },
    { label: "Learn About Our Culture", href: "#our-culture" },
  ],
};

export interface ProfDevData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const professionalDevelopment: ProfDevData = {
  eyebrow: "Professional Development",
  title: "Grow Faster Than the Market",
  intro: "At Perception X2, professional development is not a perk — it is an operational requirement. The technology evolves daily. The market evolves daily. Our team must evolve faster.",
  pillars: [
    {
      title: "Learning Architecture",
      body: "Every team member receives a structured development plan aligned with their role, career goals, and the organization's strategic needs. This is not checkbox training. This is targeted skill development that compounds.",
    },
    {
      title: "Mentorship Program",
      body: "Senior leaders across engineering, AI, strategy, and creative departments mentor emerging talent. These are not formal, bureaucratic pairings — they are working relationships built on shared projects and mutual accountability.",
    },
    {
      title: "Knowledge Sharing",
      body: "Weekly internal sessions where teams present research, findings, and operational insights. The best ideas win regardless of source. Hierarchy does not determine influence — impact does.",
    },
    {
      title: "Conference & Research Participation",
      body: "Team members present at industry conferences, publish research, and contribute to the broader AI and perception management community. External recognition reflects internal excellence.",
    },
  ],
  cta: [
    { label: "Explore Open Roles", href: "#open-roles" },
    { label: "Join Our Team", href: "#how-to-apply" },
  ],
};

export interface WorkEnvData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const workEnvironment: WorkEnvData = {
  eyebrow: "Work Environment",
  title: "Designed for Precision. Optimized for Impact.",
  intro: "Perception X2 maintains work environments that reflect our operational standards — clean, focused, and purpose-built for high-performance work. Whether you work from our operational hubs or remotely, the environment supports the precision our work demands.",
  pillars: [
    {
      title: "Operational Hubs",
      body: "Our global operational hubs provide secure, high-performance workspaces equipped with the infrastructure necessary for sensitive operations. Access controls, secure communications, and dedicated environments for classified work.",
    },
    {
      title: "Remote Operations",
      body: "For roles that support it, remote work is available with the same security protocols, communication standards, and performance expectations as in-office work. We measure outcomes, not location.",
    },
    {
      title: "Security Environment",
      body: "Every workspace — physical or digital — operates under our zero-trust security architecture. End-to-end encryption, multi-factor authentication, and strict access controls apply universally. Security is not convenient. Security is non-negotiable.",
    },
    {
      title: "Communication Infrastructure",
      body: "Real-time collaboration tools, encrypted communications, and structured reporting ensure every team member stays aligned — regardless of time zone or location. Clarity and speed in communication are operational requirements.",
    },
  ],
  cta: [
    { label: "Learn About Our Culture", href: "#our-culture" },
    { label: "Explore Benefits", href: "#benefits--compensation" },
  ],
};

export interface InterviewData {
  eyebrow: string;
  title: string;
  intro: string;
  stages: { stage: string; title: string; body: string }[];
  timeline: { label: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const interview: InterviewData = {
  eyebrow: "Interview Process",
  title: "Transparent. Rigorous. Respectful.",
  intro: "Our interview process is designed to identify exceptional talent efficiently and respectfully. We value your time. We communicate clearly. We provide feedback at every stage.",
  stages: [
    { stage: "Stage 1", title: "Application Review", body: "Submit your application with a resume and optional cover letter. Our team reviews every application individually — no automated screening. We look for demonstrated expertise, relevant experience, and alignment with our values." },
    { stage: "Stage 2", title: "Initial Conversation", body: "A 30-minute conversation with a hiring manager to discuss your background, interests, and how your skills align with the role. This is a two-way assessment — you are evaluating us as much as we are evaluating you." },
    { stage: "Stage 3", title: "Technical Assessment", body: "Role-specific technical evaluation. For engineers, this may involve system design or coding exercises. For strategists, case studies or analytical exercises. For creative roles, portfolio review and content exercises. Every assessment reflects real work you would do in the role." },
    { stage: "Stage 4", title: "Team Interviews", body: "Meet the team you would work with. These conversations assess technical depth, cultural alignment, and collaborative potential. You will meet engineers, strategists, and leadership." },
    { stage: "Stage 5", title: "Final Conversation", body: "A conversation with senior leadership to discuss career goals, compensation expectations, and any remaining questions. Full transparency on both sides." },
  ],
  timeline: [
    { label: "Most candidates complete the process within 2-3 weeks.", body: "We communicate status updates at every stage. If we are not the right fit, we will tell you — promptly and respectfully." },
  ],
  cta: [
    { label: "Apply Now", href: "#how-to-apply" },
    { label: "See Open Roles", href: "#open-roles" },
  ],
};

export interface DeiData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const diversity: DeiData = {
  eyebrow: "Diversity & Inclusion",
  title: "Excellence Knows No Boundaries",
  intro: "Perception X2 is committed to building a team that reflects the global diversity of the organizations we serve. Our platform operates across multiple continents, in 100+ languages, across 50+ platforms. The team behind it must be equally diverse in perspective, background, and experience.",
  pillars: [
    {
      title: "Our Commitment",
      body: "We hire the best people. Period. The best people come from every background, every geography, every walk of life. Excluding any talent pool based on arbitrary criteria is both morally wrong and strategically foolish.",
    },
    {
      title: "Inclusive Operations",
      body: "Our operations require diverse perspectives. Understanding how narratives resonate across cultures, demographics, and platforms demands a team that has lived those differences. Homogeneous teams produce homogeneous strategies — and homogeneous strategies fail at global scale.",
    },
    {
      title: "Equal Opportunity",
      body: "Every hiring decision is based on demonstrated capability, relevant experience, and alignment with our values. No exceptions. No asterisks.",
    },
  ],
  cta: [
    { label: "Join Our Team", href: "#how-to-apply" },
    { label: "Learn About Our Culture", href: "#our-culture" },
  ],
};

export interface LifeAtData {
  eyebrow: string;
  title: string;
  intro: string;
  quotes: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const lifeAt: LifeAtData = {
  eyebrow: "Life at Perception X2",
  title: "What Our Team Says",
  intro: "The best description of life at Perception X2 comes from the people who live it daily. These reflections represent the honest experience of working on the world's most advanced perception amplification platform.",
  quotes: [
    {
      title: "On the Work",
      body: "The problems here are genuinely hard. I have worked at three other AI companies. None of them came close to the complexity — or the impact — of what we build here. When your NLP engine deploys across 50+ platforms and you see 95-98 organic authenticity ratings in real time, you understand why this work matters.",
    },
    {
      title: "On the Culture",
      body: "There is no politics here. No games. No hidden agendas. If something needs to happen, it happens. If something is wrong, someone says so. It took me about two weeks to adjust to the directness. It took about two weeks more to realize this is the most efficient, respectful culture I have ever experienced.",
    },
    {
      title: "On Growth",
      body: "I have learned more in one year at Perception X2 than in five years at my previous company. The technology is proprietary, the problems are novel, and the senior team genuinely invests in your development. If you want to grow fast, this is the place.",
    },
    {
      title: "On Impact",
      body: "Knowing that the system you built processed 45,000 requests per second during a client's critical moment — and delivered results that changed their trajectory — that is the kind of impact you cannot find at a conventional tech company.",
    },
  ],
  cta: [
    { label: "Join Our Team", href: "#how-to-apply" },
    { label: "Explore Benefits", href: "#benefits--compensation" },
  ],
};

export interface FaqData {
  eyebrow: string;
  title: string;
  items: { q: string; a: string }[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const careersFaq: FaqData = {
  eyebrow: "FAQ",
  title: "Common Questions About Careers at Perception X2",
  items: [
    { q: "What kind of candidates does Perception X2 hire?", a: "We hire exceptional talent across engineering, artificial intelligence, machine learning, data science, strategy, operations, creative, and client success functions. We look for demonstrated expertise, intellectual rigor, and alignment with our values. Titles matter less than capability." },
    { q: "Does Perception X2 offer remote work?", a: "For eligible roles, yes. We operate a remote-first model with flexible options. Security protocols apply universally regardless of work location. Some roles — particularly those involving classified operations — require on-site work." },
    { q: "What is the interview process like?", a: "Our process typically involves five stages: application review, initial conversation, technical assessment, team interviews, and a final conversation with leadership. We communicate status updates at every stage and aim to complete the process within 2-3 weeks." },
    { q: "What compensation does Perception X2 offer?", a: "We offer market-leading base salaries, performance bonuses tied to individual and company outcomes, equity for senior roles, and comprehensive benefits. Specific compensation varies by role, experience, and location." },
    { q: "What benefits are included?", a: "Comprehensive medical, dental, and vision insurance; mental health support; wellness stipends; generous paid leave; annual learning budgets; conference attendance; mentorship programs; and premium equipment. Details vary by region." },
    { q: "Does Perception X2 sponsor visas?", a: "We evaluate visa sponsorship on a case-by-case basis depending on role requirements and location. Discuss during the interview process." },
    { q: "What is the company culture like?", a: "Direct, precision-oriented, and impact-driven. We communicate honestly, execute rigorously, and measure outcomes. The pace is demanding. The bar is high. The team is exceptional. If you thrive in environments where excellence is the standard, you will thrive here." },
    { q: "How does professional development work at Perception X2?", a: "Every team member has a structured development plan. We provide annual learning budgets, mentorship with senior leaders, internal knowledge-sharing sessions, and conference participation opportunities. Growth is not optional — it is operational." },
    { q: "What security clearances or background checks are required?", a: "Depending on role and client engagement requirements, certain roles may require security clearance or enhanced background checks. This is communicated during the interview process." },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  body: string;
  promises: string[];
  cta: { label: string; href: string }[];
  trustBar: string[];
  pillars?: { title: string; body: string }[];
}

export const careersCta: CtaData = {
  eyebrow: "Get Started",
  title: "The World's Most Advanced Platform Needs the World's Best Talent.",
  intro: "Perception X2 is building the future of autonomous perception amplification. We need exceptional engineers, AI researchers, strategists, and operators to join us.",
  body: "If you want to work on technology that does not exist anywhere else — technology that shapes narratives, protects reputations, and influences outcomes at global scale — we want to hear from you.",
  promises: [
    "Browse our open positions and submit your application.",
    "Our team reviews every application individually and provides feedback at every stage of the process.",
  ],
  cta: [
    { label: "View Open Positions", href: "#open-roles" },
    { label: "Apply Now", href: "#how-to-apply" },
    { label: "Contact Recruiting", href: "/contact" },
  ],
  trustBar: [
    "15+ Years",
    "300+ Elite Clients",
    "Nine Proprietary AI Engines",
    "Zero Security Incidents",
  ],
};
